import React, { useRef, useEffect, useState, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import { useShiftTiming } from "../../contexts/ShiftTimingMasterContext";
import Modal from "../../components/UI/Modal";
import ShiftTimingForm from "../../components/Form/ShiftTiming";
import ReusableTable from "../../components/UI/ReusableTable";
import DeleteButton from "../../components/UI/AlertDialog";
import Notification from "../../components/UI/Notification";
import Skeleton from "react-loading-skeleton";
import { Description } from "@headlessui/react";
import ReactPaginate from 'react-paginate';
import { X, Briefcase, MoreVertical, Building, Calendar, Users, MapPin, Award, Clock, Grid } from 'lucide-react';
import DropdownMenu from "../../components/UI/DropdownMenu";

const ShiftMasters = React.memo(() => {
  const { shiftTimings, addShiftTiming, updateShiftTiming, deleteShiftTiming, pagination, isLoading } = useShiftTiming();
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingShiftTiming, setEditingShiftTiming] = useState(null);
  const [message, setMessage] = useState(""); // Success message
  const [formErrors, setFormErrors] = useState({}); // Validation errors
  const [notification, setNotification] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isFiltering, setIsFiltering] = useState(false);
  const [isClient, setIsClient] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  
  // Separate component for actions column to avoid hooks rule violation
  const ActionsColumn = React.memo(({ row, openMenu, setOpenMenu, openModalForEdit, handleDeleteApproverModule }) => {
    const buttonRef = useRef(null);
    
    return (
      <div className="flex items-center justify-center relative">
        <button
          ref={buttonRef}
          onClick={() =>
            setOpenMenu(openMenu === row.id ? null : row.id)
          }
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
        >
          <MoreVertical className="w-4 h-4 text-gray-600 dark:text-gray-400" />
        </button>

        {openMenu === row.id && (
          <DropdownMenu
            buttonRef={buttonRef}
            onClose={() => setOpenMenu(null)}
          >
            <button
              onClick={(e) => {
                openModalForEdit(row);
                setOpenMenu(null);
              }}
              className="w-full text-left px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
            >
              Edit
            </button>
            <DeleteButton
              style={`text-red-600 hover:text-red-800 px-3 py-2`}
              onDelete={(e) => {
                handleDeleteApproverModule(row.id);
                setOpenMenu(null);
              }}
            />
          </DropdownMenu>
        )}
      </div>
    );
  });

  useEffect(() => {
    setIsClient(true); // Ensures document.body is available
  }, []);

  // Handlers
  const openModalForAdd = useCallback(() => {
    setEditingShiftTiming(null);
    setIsModalOpen(true);
    setMessage("");
    setFormErrors({});
  }, []);

  const openModalForEdit = useCallback((shiftTiming) => {
    setEditingShiftTiming(shiftTiming);
    setIsModalOpen(true);
    setMessage("");
    setFormErrors({});
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setEditingShiftTiming(null);
    setFormErrors({});
    setMessage("");
  }, []);

  const convertFormDataToObject = (formData) => {
    return Object.fromEntries(formData.entries());
  };

  const handleFormSubmit = useCallback(
    async (shiftTiming) => {
      setLoading(true);
      try {
        if (editingShiftTiming) {
          // console.log("shift-timing payload", shiftTiming);
        
          // Convert FormData to object
          // console.log(shiftTiming instanceof FormData)
          const shiftTimingData = shiftTiming instanceof FormData ? convertFormDataToObject(shiftTiming) : shiftTiming;
  
          // console.log("Payload being sent:", { ...editingShiftTiming, ...shiftTimingData });
  
          await updateShiftTiming({ ...editingShiftTiming, ...shiftTimingData });
          setMessage("Shift Timing updated successfully.");
          // setNotification("ShiftTiming updated successfully.");
          setNotification({
            message: "Shift Timing updated successfully.",
            type: "success",
          });
        } else {
          // console.log("create shift-timing", shiftTiming)
          await addShiftTiming(shiftTiming);
          setMessage("Shift Timing added successfully.");
          // setNotification("ShiftTiming added successfully.");
          setNotification({
            message: "Shift Timing added successfully.",
            type: "success",
          });
        }
        closeModal();
      } catch (err) {
        if (err.errors) {
          setFormErrors(err.errors);
        } else {
          // console.error("Unexpected error during submission:", err);
          // setNotification("Unexpected error during submission:", err);
          setNotification({
            message: "Unexpected error during submission:", err,
            type: "error",
          });
        }
      } finally {
        setLoading(false); 
      }
    },
    [editingShiftTiming, addShiftTiming, updateShiftTiming, closeModal]
  );

  // Memoize candidates with campaign names for the table
  const shiftTimingForTable = useMemo(
    () =>
        shiftTimings.map((shiftTiming) => ({
        ...shiftTiming,
        name: shiftTiming?.name || "Unknown Shift Timing",
      })),
    [shiftTimings]
  );

  const handleDeleteShiftTiming = async (id) => {
    try {
      await deleteShiftTiming(id);
      // setNotification("ShiftTiming deleted successfully");
      setNotification({
        message: "ShiftTiming deleted successfully",
        type: "success",
      });
    } catch (error) {
      // setNotification("Failed to delete shift-type");
      setNotification({
        message: "Failed to delete shift-type",
        type: "error",
      });
    }
  };

  // Table columns
  const columns = useMemo(
    () => [
      { label: "Name", field: "name" },
      {
        label: "Actions",
        field: "actions",
        render: (row) => (
          <ActionsColumn
            row={row}
            openMenu={openMenu}
            setOpenMenu={setOpenMenu}
            openModalForEdit={openModalForEdit}
            handleDeleteApproverModule={handleDeleteShiftTiming}
          />
        ),
      },
    ],
    [
      openMenu,
      openModalForEdit,
      handleDeleteShiftTiming,
    ]
  );

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setCurrentPage(newPage);
      shiftTimings(newPage);
    }
  };


  return (
    <div className="px-3 py-2 bg-white dark:bg-gray-900 font-poppins rounded-lg">
      
      {/* notification compo */}
      {notification && (
        <Notification message={notification.message} type={notification.type} onClose={() => setNotification(null)} />
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h2 className="text-2xl font-semibold text-black dark:text-white">Shift-Timing Masters</h2>
        <button
          onClick={openModalForAdd}
          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700">
          Add Shift-Timing
        </button>
      </div>

      {/* Success Message */}
      {message && (
        <div className="mb-4 p-4 bg-green-100 border border-green-500 text-green-700 rounded-md">
          {message}
        </div>
      )}

      {/* Skeleton Loader */}
      <div>
        <ReusableTable
          data={shiftTimingForTable}
          // data={requests.slice().reverse()}
          loading={isLoading}
          columns={columns}
          filterable={true}
          excludeFilterColumns={["actions"]}
          onFilterChange={(filtering) => setIsFiltering(filtering)}
          // excludeFilterColumns={excludeFilterColumns}
        />

        {/* Pagination */}
        {!isFiltering && shiftTimingForTable?.length > 0 &&  (
          <ReactPaginate
            breakLabel="..."
            onPageChange={(event) => handlePageChange(event.selected + 1)}
            pageRangeDisplayed={5}
            pageCount={pagination.totalPages}
            renderOnZeroPageCount={null}
            nextLabel="Next >"
            previousLabel="< Prev"
            previousClassName='dark:text-white px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-800'
            nextClassName="dark:text-white px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-800"
            containerClassName="flex items-center justify-center mt-4"
            pageClassName="mx-1 px-3 py-1 border rounded dark:text-white"
            activeClassName="bg-blue-500 text-white dark:bg-black dark:text-white dark:border-gray-700"
          />
        )}
      </div>

      {/* Modal for Adding or Editing */}
      <Modal
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        title={editingShiftTiming ? "Edit Shift-Timing" : "Add Shift-Timing"}>
        <ShiftTimingForm
          loading={loading}
          closeModal={closeModal}
          onSubmit={handleFormSubmit}
          initialValues={editingShiftTiming || { name: "", description: "" }}
          errors={formErrors}
        />
      </Modal>
    </div>
  );
});

export default ShiftMasters;
